import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Trophy, Award, Calendar, BarChart3, ExternalLink, Pencil } from 'lucide-react';
import { format, subWeeks, subMonths, subYears } from 'date-fns';
import { useI18n } from '../i18n/context';
import { getDateFnsLocale } from '../lib/localeRuntime';
import { getClientExerciseHistory, getClientExerciseWorkingWeights, updateClientExerciseWorkingWeight } from '../services/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Calculate volume for a set: weight × reps
 * @param {number} weight - Weight lifted
 * @param {number} reps - Number of repetitions
 * @returns {number} Volume
 */
const calculateSetVolume = (weight, reps) => {
  if (!weight || !reps) return 0;
  return weight * reps;
};

/**
 * Calculate total session volume: sum of all set volumes
 * @param {Array} sets - Array of sets with weight and reps
 * @returns {number} Total volume
 */
const calculateSessionVolume = (sets) => {
  if (!sets || !Array.isArray(sets)) return 0;
  return sets.reduce((total, set) => {
    return total + calculateSetVolume(set.weight, set.reps);
  }, 0);
};

const ExerciseAnalyticsModal = ({
  show,
  onHide,
  clientId,
  exerciseName,
  exerciseDisplayName,
  maxWeightHistory,
  onOpenWorkout,
}) => {
  const { t, locale } = useI18n();
  const dateLocale = getDateFnsLocale(locale);
  const displayExerciseName = exerciseDisplayName || exerciseName;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('heaviest'); // 'heaviest' | 'volume'
  const [timeRange, setTimeRange] = useState('all'); // 'week', 'month', 'year', 'all'
  const [openSessions, setOpenSessions] = useState(new Set()); // Track which sessions are open
  const [workingWeightRow, setWorkingWeightRow] = useState(null);
  const [workingWeightLoading, setWorkingWeightLoading] = useState(false);
  const [showEditWorkingWeight, setShowEditWorkingWeight] = useState(false);
  const [workingWeightInput, setWorkingWeightInput] = useState('');
  const [isSavingWorkingWeight, setIsSavingWorkingWeight] = useState(false);
  const [workingWeightError, setWorkingWeightError] = useState('');

  useEffect(() => {
    if (show && clientId && exerciseName) {
      loadHistory();
    }
  }, [show, clientId, exerciseName]);

  const toNum = (v) => {
    if (typeof v === 'number' && isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const p = parseFloat(v.replace(',', '.'));
      return !isNaN(p) && isFinite(p) ? p : null;
    }
    return null;
  };

  const loadWorkingWeight = async () => {
    if (!clientId || !exerciseName) return;
    setWorkingWeightLoading(true);
    try {
      const resp = await getClientExerciseWorkingWeights(clientId, [exerciseName]);
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const row = rows.find((r) => r?.exercise_name === exerciseName) || rows[0] || null;
      if (row) {
        setWorkingWeightRow({
          ...row,
          manual_working_weight: toNum(row.manual_working_weight),
          max_history_weight: toNum(row.max_history_weight),
          working_weight: toNum(row.working_weight),
        });
      } else {
        setWorkingWeightRow(null);
      }
    } catch (e) {
      console.error('[ExerciseAnalyticsModal] Failed to load working weight:', e);
      setWorkingWeightRow(null);
    } finally {
      setWorkingWeightLoading(false);
    }
  };

  useEffect(() => {
    if (show && clientId && exerciseName) {
      loadWorkingWeight();
    } else {
      setWorkingWeightRow(null);
      setWorkingWeightError('');
      setWorkingWeightInput('');
      setShowEditWorkingWeight(false);
    }
  }, [show, clientId, exerciseName]);

  const loadHistory = async () => {
    if (!clientId || !exerciseName) return;
    
    setLoading(true);
    try {
      const response = await getClientExerciseHistory(clientId, exerciseName);
      const data = response.data || [];
      // Sort by date descending (newest first)
      const sortedData = [...data].sort((a, b) => {
        const dateA = a.date ? new Date(`${a.date} ${a.time || ''}`) : new Date(0);
        const dateB = b.date ? new Date(`${b.date} ${b.time || ''}`) : new Date(0);
        return dateB - dateA;
      });
      setHistory(sortedData);
    } catch (error) {
      console.error('Error loading exercise history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const normalizeYmd = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    // Handle ISO timestamps like "2025-12-17T00:00:00.000Z"
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : s;
  };

  const parseWorkoutDateTimeLocal = (dateStrRaw, timeStrRaw) => {
    const dateStr = normalizeYmd(dateStrRaw);
    if (!dateStr) return null;

    // Prefer local parsing for YYYY-MM-DD to avoid timezone shifts
    const ymd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    let d = null;
    if (ymd) {
      const [, y, m, day] = ymd;
      d = new Date(Number(y), Number(m) - 1, Number(day), 0, 0, 0, 0);
    } else {
      // Fallback: best-effort parse, but may be timezone-sensitive
      d = new Date(dateStr);
      if (isNaN(d.getTime())) return null;
    }

    const timeStr = typeof timeStrRaw === 'string' ? timeStrRaw : '';
    const tm = timeStr.match(/^(\d{1,2}):(\d{2})/);
    if (tm) {
      const [, hh, mm] = tm;
      d.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
    }

    return isNaN(d.getTime()) ? null : d;
  };

  // Process history data for calculations
  const processedData = useMemo(() => {
    if (!history || history.length === 0) return { sessions: [], records: null, setRecords: {} };

    const sessions = history.map(session => {
      const sets = session.sets || [];
      const date = parseWorkoutDateTimeLocal(session.date, session.time);
      
      // Calculate metrics for this session
      let heaviestWeight = 0;
      let bestSetVolume = 0;
      
      sets.forEach(set => {
        const weight = set.weight || 0;
        const reps = set.reps || 0;
        
        // Heaviest weight
        if (weight > heaviestWeight) {
          heaviestWeight = weight;
        }
        
        // Best set volume
        const setVol = calculateSetVolume(weight, reps);
        if (setVol > bestSetVolume) {
          bestSetVolume = setVol;
        }
      });
      
      const sessionVolume = calculateSessionVolume(sets);
      
      return {
        ...session,
        date,
        dateStr: normalizeYmd(session.date),
        timeStr: session.time,
        heaviestWeight,
        bestSetVolume,
        sessionVolume,
        sets
      };
    });

    // Calculate personal records
    const records = sessions.reduce((acc, session) => {
      if (session.heaviestWeight > (acc.heaviestWeight || 0)) {
        acc.heaviestWeight = session.heaviestWeight;
      }
      if (session.bestSetVolume > (acc.bestSetVolume || 0)) {
        acc.bestSetVolume = session.bestSetVolume;
      }
      if (session.sessionVolume > (acc.bestSessionVolume || 0)) {
        acc.bestSessionVolume = session.sessionVolume;
      }
      return acc;
    }, {
      heaviestWeight: 0,
      bestSetVolume: 0,
      bestSessionVolume: 0
    });

    // Update heaviest weight from maxWeightHistory if available
    if (maxWeightHistory && maxWeightHistory.weight) {
      if (maxWeightHistory.weight > records.heaviestWeight) {
        records.heaviestWeight = maxWeightHistory.weight;
      }
    }

    // Calculate set records (best weight for different rep ranges)
    const repRanges = [1, 3, 5, 8, 10, 12];
    const setRecords = {};
    
    repRanges.forEach(targetReps => {
      let bestWeight = 0;
      sessions.forEach(session => {
        session.sets.forEach(set => {
          const reps = set.reps || 0;
          const weight = set.weight || 0;
          // Check if reps are within range (targetReps ± 1 for flexibility)
          if (Math.abs(reps - targetReps) <= 1 && weight > bestWeight) {
            bestWeight = weight;
          }
        });
      });
      if (bestWeight > 0) {
        setRecords[targetReps] = bestWeight;
      }
    });

    return { sessions, records, setRecords };
  }, [history, maxWeightHistory]);

  // Filter data by time range
  const filteredSessions = useMemo(() => {
    if (!processedData.sessions || processedData.sessions.length === 0) return [];
    
    if (timeRange === 'all') return processedData.sessions;
    
    const now = new Date();
    let cutoffDate;
    
    switch (timeRange) {
      case 'week':
        cutoffDate = subWeeks(now, 1);
        break;
      case 'month':
        cutoffDate = subMonths(now, 1);
        break;
      case 'year':
        cutoffDate = subYears(now, 1);
        break;
      default:
        return processedData.sessions;
    }
    
    return processedData.sessions.filter(session => {
      if (!session.date) return false;
      return session.date >= cutoffDate;
    });
  }, [processedData.sessions, timeRange]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const dataPoints = [];

    // Add session data points
    if (filteredSessions && filteredSessions.length > 0) {
      filteredSessions.forEach(session => {
        const date = session.date;
        if (!date) return;

        let value = 0;
        switch (selectedMetric) {
          case 'heaviest':
            value = session.heaviestWeight || 0;
            break;
          case 'volume':
            value = session.sessionVolume || 0;
            break;
          default:
            value = 0;
        }

        dataPoints.push({
          date: format(date, 'P', { locale: dateLocale }),
          dateISO: date.toISOString(),
          value: Math.round(value * 100) / 100, // Round to 2 decimals
          fullDate: date,
          type: 'session'
        });
      });
    }

    // Add max weight history point if available and metric is 'heaviest'
    if (selectedMetric === 'heaviest' && maxWeightHistory && maxWeightHistory.weight && maxWeightHistory.date) {
      try {
        const maxWeightDate = new Date(maxWeightHistory.date);
        if (!isNaN(maxWeightDate.getTime())) {
          // Check if this date is within the selected time range
          const now = new Date();
          let shouldInclude = true;
          
          if (timeRange !== 'all') {
            let cutoffDate;
            switch (timeRange) {
              case 'week':
                cutoffDate = subWeeks(now, 1);
                break;
              case 'month':
                cutoffDate = subMonths(now, 1);
                break;
              case 'year':
                cutoffDate = subYears(now, 1);
                break;
              default:
                shouldInclude = true;
            }
            if (cutoffDate && maxWeightDate < cutoffDate) {
              shouldInclude = false;
            }
          }

          if (shouldInclude) {
            dataPoints.push({
              date: format(maxWeightDate, 'P', { locale: dateLocale }),
              dateISO: maxWeightDate.toISOString(),
              value: Math.round(maxWeightHistory.weight * 100) / 100,
              fullDate: maxWeightDate,
              type: 'maxWeight'
            });
          }
        }
      } catch (e) {
        console.error('Error parsing max weight date:', e);
      }
    }

    return dataPoints
      .filter(item => item !== null)
      .sort((a, b) => a.fullDate - b.fullDate); // Sort chronologically for chart
  }, [filteredSessions, selectedMetric, maxWeightHistory, timeRange, dateLocale]);

  const metricAxisLabel = useMemo(() => {
    if (selectedMetric === 'heaviest') return t('exerciseAnalytics.metricHeaviestAxis');
    if (selectedMetric === 'volume') return t('exerciseAnalytics.metricVolumeAxis');
    return '';
  }, [selectedMetric, t]);

  const formatKg = useCallback(
    (value) => t('exerciseAnalytics.kgValue', { value: Math.round(value * 10) / 10 }),
    [t]
  );

  if (!show) return null;

  return (
    <FullScreenModal open={show} onOpenChange={onHide}>
      <FullScreenModalContent className="flex flex-col">
        <FullScreenModalHeader>
          <FullScreenModalBackButton onClick={onHide} />
          <FullScreenModalTitle>{displayExerciseName}</FullScreenModalTitle>
          <div className="w-11 h-11" />
        </FullScreenModalHeader>

        <FullScreenModalBody>
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="summary">{t('exerciseAnalytics.tabSummary')}</TabsTrigger>
            <TabsTrigger value="history">{t('exerciseAnalytics.tabHistory')}</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-6 mt-4">
            {/* Interactive Progress Graph */}
            <Card>
              <CardHeader>
                <CardTitle>{t('exerciseAnalytics.progressTitle')}</CardTitle>
                <CardDescription>{t('exerciseAnalytics.progressDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Metric Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant={selectedMetric === 'heaviest' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedMetric('heaviest')}
                  >
                    {t('exerciseAnalytics.metricHeaviestShort')}
                  </Button>
                  <Button
                    variant={selectedMetric === 'volume' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedMetric('volume')}
                  >
                    {t('exerciseAnalytics.metricVolumeShort')}
                  </Button>
                </div>

                {/* Time Range Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant={timeRange === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeRange('week')}
                  >
                    {t('exerciseAnalytics.timeWeek')}
                  </Button>
                  <Button
                    variant={timeRange === 'month' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeRange('month')}
                  >
                    {t('exerciseAnalytics.timeMonth')}
                  </Button>
                  <Button
                    variant={timeRange === 'year' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeRange('year')}
                  >
                    {t('exerciseAnalytics.timeYear')}
                  </Button>
                  <Button
                    variant={timeRange === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeRange('all')}
                  >
                    {t('exerciseAnalytics.timeAll')}
                  </Button>
                </div>

                {/* Chart */}
                {loading ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    {t('exerciseAnalytics.loadingChart')}
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        label={{ value: metricAxisLabel, angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => {
                          const label = props.payload?.type === 'maxWeight' 
                            ? t('exerciseAnalytics.tooltipHeaviestUpdated') 
                            : metricAxisLabel;
                          return [formatKg(value), label];
                        }}
                        labelFormatter={(label) => t('exerciseAnalytics.tooltipDate', { date: label })}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#0088FE" 
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          // Highlight max weight points with different color
                          if (payload?.type === 'maxWeight') {
                            return <circle cx={cx} cy={cy} r={6} fill="#10b981" stroke="#059669" strokeWidth={2} />;
                          }
                          return <circle cx={cx} cy={cy} r={4} fill="#0088FE" />;
                        }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    {t('exerciseAnalytics.noChartData')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Personal Records */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  {t('exerciseAnalytics.recordsTitle')}
                </CardTitle>
                <CardDescription>{t('exerciseAnalytics.recordsDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-muted-foreground">{t('exerciseAnalytics.loadingShort')}</div>
                ) : processedData.records ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">{t('exerciseAnalytics.workingWeight')}</span>
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-muted transition-colors"
                          onClick={() => {
                            const initial = workingWeightRow?.manual_working_weight ?? workingWeightRow?.working_weight ?? null;
                            setWorkingWeightInput(
                              typeof initial === 'number' && isFinite(initial) ? String(initial) : ''
                            );
                            setWorkingWeightError('');
                            setShowEditWorkingWeight(true);
                          }}
                          title={t('exerciseAnalytics.editWorkingWeightTitle')}
                          aria-label={t('exerciseAnalytics.editWorkingWeightTitle')}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                      <span className="text-lg font-bold">
                        {workingWeightLoading ? '…' : (() => {
                          const ww = workingWeightRow?.working_weight;
                          if (typeof ww === 'number' && isFinite(ww) && ww > 0) return formatKg(ww);
                          const fallback = processedData.records.heaviestWeight;
                          return fallback > 0 ? formatKg(fallback) : t('exerciseAnalytics.emDash');
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-purple-500" />
                        <span className="text-sm font-medium">{t('exerciseAnalytics.bestSetVolume')}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {processedData.records.bestSetVolume > 0 
                          ? formatKg(processedData.records.bestSetVolume) 
                          : t('exerciseAnalytics.emDash')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-orange-500" />
                        <span className="text-sm font-medium">{t('exerciseAnalytics.bestSessionVolume')}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {processedData.records.bestSessionVolume > 0 
                          ? formatKg(processedData.records.bestSessionVolume) 
                          : t('exerciseAnalytics.emDash')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">{t('exerciseAnalytics.noData')}</div>
                )}
              </CardContent>
            </Card>

            {/* Edit working weight dialog */}
            <AlertDialog open={showEditWorkingWeight} onOpenChange={setShowEditWorkingWeight}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('exerciseAnalytics.editWwDialogTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('exerciseAnalytics.editWwDialogDesc', { name: displayExerciseName })}
                  </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-2">
                  <Label htmlFor="edit-working-weight">{t('exerciseAnalytics.labelWwKg')}</Label>
                  <Input
                    id="edit-working-weight"
                    type="number"
                    step="0.1"
                    min="0"
                    value={workingWeightInput}
                    onChange={(e) => setWorkingWeightInput(e.target.value)}
                    placeholder={t('exerciseAnalytics.phWw')}
                  />
                  {workingWeightError ? (
                    <div className="text-sm text-destructive">{workingWeightError}</div>
                  ) : null}
                </div>

                  <AlertDialogFooter>
                  <AlertDialogCancel disabled={isSavingWorkingWeight}>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isSavingWorkingWeight}
                    onClick={async (e) => {
                      // Prevent dialog from closing on validation errors
                      e?.preventDefault?.();

                      if (!clientId || !exerciseName) return;

                      setIsSavingWorkingWeight(true);
                      setWorkingWeightError('');
                      try {
                        const raw = (workingWeightInput || '').trim();
                        const payloadVal = raw === '' ? null : raw;
                        if (payloadVal !== null) {
                          const parsed = parseFloat(String(payloadVal).replace(',', '.'));
                          if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
                            setWorkingWeightError(t('exerciseAnalytics.errInvalidWeight'));
                            return;
                          }
                        }

                        await updateClientExerciseWorkingWeight(clientId, exerciseName, payloadVal);
                        await loadWorkingWeight();
                        setShowEditWorkingWeight(false);
                      } catch (err) {
                        console.error('[ExerciseAnalyticsModal] Failed to update working weight:', err);
                        const msg =
                          err?.response?.data?.error ||
                          err?.response?.data?.message ||
                          err?.message ||
                          t('exerciseAnalytics.errUpdateWeight');
                        setWorkingWeightError(msg);
                      } finally {
                        setIsSavingWorkingWeight(false);
                      }
                    }}
                  >
                    {isSavingWorkingWeight ? t('exerciseAnalytics.saving') : t('optForm.save')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

          </TabsContent>

          <TabsContent value="history" className="space-y-4 mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('exerciseAnalytics.loadingHistory')}
              </div>
            ) : processedData.sessions.length > 0 ? (
              <div className="space-y-2">
                {processedData.sessions.map((session, index) => {
                  const dateStr = session.dateStr || t('exerciseAnalytics.unknownDate');
                  const timeStr = session.timeStr || '';
                  const formattedDate = session.date 
                    ? format(session.date, 'P', { locale: dateLocale })
                    : dateStr;
                  
                  const sessionKey = session.id || `session-${index}`;
                  const isOpen = openSessions.has(sessionKey);
                  
                  return (
                    <AccordionItem key={sessionKey}>
                      <AccordionTrigger
                        isOpen={isOpen}
                        onClick={(newState) => {
                          setOpenSessions(prev => {
                            const newSet = new Set(prev);
                            if (newState) {
                              newSet.add(sessionKey);
                            } else {
                              newSet.delete(sessionKey);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div className="flex items-center w-full pr-2 gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{formattedDate}</span>
                            {timeStr && (
                              <span className="text-sm text-muted-foreground whitespace-nowrap ml-3">{timeStr}</span>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 flex-shrink-0"
                            disabled={!session?.workout_id}
                            aria-label={t('exerciseAnalytics.openWorkout')}
                            title={t('exerciseAnalytics.openWorkout')}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (typeof onOpenWorkout === 'function' && session?.workout_id) {
                                onOpenWorkout(session);
                              }
                              if (typeof onHide === 'function') onHide();
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent isOpen={isOpen}>
                        <div className="space-y-2 pt-2">
                          {session.sets && session.sets.length > 0 ? (
                            session.sets
                              .sort((a, b) => (a.set_number || 0) - (b.set_number || 0))
                              .map((set, setIndex) => (
                                <div 
                                  key={set.id || setIndex} 
                                  className="flex items-center justify-between p-2 border rounded"
                                >
                                  <span className="text-sm font-medium">
                                    {t('exerciseAnalytics.setN', { n: set.set_number || setIndex + 1 })}
                                  </span>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span>
                                      <span className="text-muted-foreground">{t('exerciseAnalytics.weight')}: </span>
                                      <span className="font-medium">
                                        {set.weight !== null && set.weight !== undefined 
                                          ? formatKg(Number(set.weight)) 
                                          : t('exerciseAnalytics.emDash')}
                                      </span>
                                    </span>
                                    <span>
                                      <span className="text-muted-foreground">{t('exerciseAnalytics.reps')}: </span>
                                      <span className="font-medium">{set.reps || 0}</span>
                                    </span>
                                  </div>
                                </div>
                              ))
                          ) : (
                            <div className="text-sm text-muted-foreground py-2">
                              {t('exerciseAnalytics.noSetData')}
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t('exerciseAnalytics.noHistory')}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </FullScreenModalBody>
      </FullScreenModalContent>
    </FullScreenModal>
  );
};

export default ExerciseAnalyticsModal;

